import type { GooglePlace } from "@albertine/types";
import type {
    CatalogItemImageReference,
    CatalogItemTag,
    CatalogItemTranslation,
    CreateCatalogItemRequest,
    GeoPoint,
    UpdateCatalogItemRequest,
} from "@albertine/types/openapi/lmt";
import { CatalogItemTagSchema } from "@albertine/types/openapi/lmt";
import type { CatalogItemFormSchema } from "../../common/CatalogItem";
import type { CatalogItemEditableTranslation } from "../../common/types/Translation";
import { ifEmptyDefaultToNull } from "../../common/utils/string.util";
import { allowCategorySpecificTagsOnly } from "../../common/utils/tags.util";

type CatalogItemWriteFields = Omit<CreateCatalogItemRequest, "imageFileUploadIds">;

export function toCreateCatalogItemRequest(
    form: CatalogItemFormSchema,
    imageFileUploadIds: string[],
): CreateCatalogItemRequest {
    return {
        ...toCatalogItemWriteFields(form),
        imageFileUploadIds,
    };
}

export function toUpdateCatalogItemRequest(
    form: CatalogItemFormSchema,
    images: CatalogItemImageReference[],
): UpdateCatalogItemRequest {
    return {
        ...toCatalogItemWriteFields(form),
        images,
    };
}

function toCatalogItemWriteFields(form: CatalogItemFormSchema): CatalogItemWriteFields {
    const googlePlaceData = toGooglePlaceData(form.googlePlaceData);
    return {
        name: form.name,
        description: form.description,
        notes: ifEmptyDefaultToNull(form.notes),
        source: form.source,
        googlePlaceId: ifEmptyDefaultToNull(form.googlePlaceId),
        address: ifEmptyDefaultToNull(form.address),
        addressComponents: form.addressComponents ?? null,
        website: ifEmptyDefaultToNull(form.website),
        phoneNumber: ifEmptyDefaultToNull(form.phoneNumber),
        googleUrl: ifEmptyDefaultToNull(form.googleUrl),
        locationGeoPoint: toLocationGeoPoint(form.geoPoint),
        offeringCategories: [form.offeringCategory],
        tags: toCatalogItemTags(
            allowCategorySpecificTagsOnly(form.tags ?? [], form.offeringCategory),
        ),
        supplierLmtIds: form.suppliers ?? [],
        localInsights: ifEmptyDefaultToNull(form.localInsights),
        published: form.published,
        headline: ifEmptyDefaultToNull(form.headline),
        benefits: ifEmptyDefaultToNull(form.benefits),
        bookingPolicy: ifEmptyDefaultToNull(form.bookingPolicy),
        albertineVerified: form.albertineVerified ?? null,
        recentlyOpened: form.recentlyOpened ?? null,
        eventDates: ifEmptyDefaultToNull(form.eventDates),
        travelItineraryNotes: ifEmptyDefaultToNull(form.travelItineraryNotes),
        translations: toTranslations(form.translations),
        discoverableForTenant: form.discoverableForTenant ?? null,
        ...(googlePlaceData ? { googlePlaceData } : {}),
    };
}

function toTranslations(
    translations: CatalogItemFormSchema["translations"],
): CreateCatalogItemRequest["translations"] {
    const de = toTranslation(translations?.de);
    const fr = toTranslation(translations?.fr);
    if (!de && !fr) {
        return undefined;
    }
    return {
        ...(de ? { de } : {}),
        ...(fr ? { fr } : {}),
    };
}

function toTranslation(
    translation: CatalogItemEditableTranslation | undefined,
): CatalogItemTranslation | undefined {
    if (!translation) {
        return undefined;
    }
    return {
        name: ifEmptyDefaultToNull(translation.name),
        description: ifEmptyDefaultToNull(translation.description),
        address: ifEmptyDefaultToNull(translation.address),
        headline: ifEmptyDefaultToNull(translation.headline),
        localInsights: ifEmptyDefaultToNull(translation.localInsights),
        benefits: ifEmptyDefaultToNull(translation.benefits),
        bookingPolicy: ifEmptyDefaultToNull(translation.bookingPolicy),
        eventDates: ifEmptyDefaultToNull(translation.eventDates),
        travelItineraryNotes: ifEmptyDefaultToNull(translation.travelItineraryNotes),
    };
}

function toLocationGeoPoint(
    geoPoint: CatalogItemFormSchema["geoPoint"],
): GeoPoint | null {
    if (
        typeof geoPoint?.latitude !== "number" ||
        typeof geoPoint.longitude !== "number"
    ) {
        return null;
    }
    return {
        latitude: geoPoint.latitude,
        longitude: geoPoint.longitude,
    };
}

function toCatalogItemTags(tags: readonly string[]): CatalogItemTag[] {
    return tags.flatMap((tag) => {
        const parsed = CatalogItemTagSchema.safeParse(tag);
        return parsed.success ? [parsed.data] : [];
    });
}

function toGooglePlaceData(
    place: GooglePlace | null | undefined,
): CreateCatalogItemRequest["googlePlaceData"] {
    if (!place) {
        return undefined;
    }
    return place;
}
